import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  regulationScopeLabel,
  type ProjectSummary,
  type RegulationScope,
  type ScanStatus,
} from '../types';
import {
  filterAndSortProjects,
  projectWorkspaceCardPresentation,
  type ProjectWorkspaceSort,
} from '../pages/projectWorkspacePresentation';

interface ProjectHistorySectionProps {
  projects: ProjectSummary[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onOpen: (project: ProjectSummary) => void;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, yyyy HH:mm');
}

export default function ProjectHistorySection({
  projects,
  isLoading,
  error,
  onRetry,
  onOpen,
}: ProjectHistorySectionProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ScanStatus | 'all'>('all');
  const [scope, setScope] = useState<RegulationScope | 'all'>('all');
  const [sort, setSort] = useState<ProjectWorkspaceSort>('updated_desc');
  const visibleProjects = useMemo(
    () => filterAndSortProjects(projects, { search, status, scope, sort }),
    [projects, search, scope, sort, status]
  );
  const scopes = useMemo(
    () => Array.from(new Set(projects.map(project => project.regulationScope))),
    [projects]
  );

  if (isLoading) {
    return (
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div
            className="animate-spin"
            style={{
              width: 32,
              height: 32,
              border: '3px solid var(--border)',
              borderTopColor: 'var(--accent-primary)',
              borderRadius: '50%',
            }}
          />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        role="alert"
        style={{
          marginBottom: 32,
          padding: 20,
          background: 'var(--danger-dim)',
          border: '1px solid rgba(255,23,68,0.2)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--danger)',
        }}
      >
        <div style={{ marginBottom: 14 }}>{error}</div>
        <button className="button button-secondary" onClick={onRetry}>
          {t('history.projects.retry')}
        </button>
      </section>
    );
  }

  if (projects.length === 0) return null;

  return (
    <section style={{ marginBottom: 36 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 14,
        }}
      >
        <h2
          style={{
            margin: 0,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-display)',
            fontSize: 18,
          }}
        >
          {t('history.projects.title')}
        </h2>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {t('history.projects.count', { count: visibleProjects.length })}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <label style={filterLabelStyle}>
          Search project name
          <input
            aria-label="Search project name"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search projects"
            style={filterControlStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          Scan status
          <select
            aria-label="Filter by scan status"
            value={status}
            onChange={event =>
              setStatus(event.target.value as ScanStatus | 'all')
            }
            style={filterControlStyle}
          >
            <option value="all">All statuses</option>
            {(['done', 'processing', 'error', 'pending'] as ScanStatus[]).map(
              value => (
                <option key={value} value={value}>
                  {t(`history.status.${value}`)}
                </option>
              )
            )}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Regulation scope
          <select
            aria-label="Filter by regulation scope"
            value={scope}
            onChange={event =>
              setScope(event.target.value as RegulationScope | 'all')
            }
            style={filterControlStyle}
          >
            <option value="all">All scopes</option>
            {scopes.map(value => (
              <option key={value} value={value}>
                {regulationScopeLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Sort projects
          <select
            aria-label="Sort projects"
            value={sort}
            onChange={event =>
              setSort(event.target.value as ProjectWorkspaceSort)
            }
            style={filterControlStyle}
          >
            <option value="updated_desc">Recently updated</option>
            <option value="name_asc">Name A–Z</option>
            <option value="gaps_desc">Most unresolved gaps</option>
            <option value="tasks_desc">Most open tasks</option>
          </select>
        </label>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 14,
        }}
      >
        {visibleProjects.map(project => {
          const checklistPercent =
            project.checklistTotal > 0
              ? Math.round(
                  (project.checklistCompleted / project.checklistTotal) * 100
                )
              : 0;
          const processing =
            project.latestScanStatus === 'processing' ||
            project.latestScanStatus === 'pending';
          const cardPresentation = projectWorkspaceCardPresentation(project);

          return (
            <article
              key={project.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                padding: 20,
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h3
                    title={project.name}
                    style={{
                      margin: 0,
                      overflow: 'hidden',
                      color: 'var(--text-primary)',
                      fontSize: 17,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {project.name}
                  </h3>
                  <div
                    style={{
                      marginTop: 4,
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      lineHeight: 1.4,
                    }}
                  >
                    {regulationScopeLabel(project.regulationScope)}
                  </div>
                </div>

                {project.latestScanStatus && (
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '3px 8px',
                      color: processing
                        ? 'var(--warning)'
                        : project.latestScanStatus === 'error'
                          ? 'var(--danger)'
                          : 'var(--success)',
                      background: processing
                        ? 'var(--warning-dim)'
                        : project.latestScanStatus === 'error'
                          ? 'var(--danger-dim)'
                          : 'var(--success-dim)',
                      borderRadius: 100,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t(`history.status.${project.latestScanStatus}`)}
                  </span>
                )}
              </div>

              <div
                style={{
                  marginBottom: 18,
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                <div
                  title={project.latestUploadFilename}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {project.latestUploadFilename ??
                    t('history.projects.noUpload')}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    color: 'var(--text-muted)',
                    fontSize: 11,
                  }}
                >
                  {t('history.projects.latestUpload')}:{' '}
                  {formatDate(project.latestUploadAt)}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 7,
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                  }}
                >
                  <span>{t('history.projects.checklist')}</span>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {project.checklistCompleted}/{project.checklistTotal}
                  </strong>
                </div>
                <div
                  role="progressbar"
                  aria-label={t('history.projects.checklistProgress', {
                    completed: project.checklistCompleted,
                    total: project.checklistTotal,
                  })}
                  aria-valuemin={0}
                  aria-valuemax={Math.max(project.checklistTotal, 1)}
                  aria-valuenow={project.checklistCompleted}
                  style={{
                    height: 7,
                    overflow: 'hidden',
                    background: 'var(--bg-tertiary)',
                    borderRadius: 999,
                  }}
                >
                  <div
                    style={{
                      width: `${checklistPercent}%`,
                      height: '100%',
                      background: 'var(--accent-primary)',
                      borderRadius: 999,
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginTop: 'auto',
                  marginBottom: 16,
                  color: 'var(--text-muted)',
                  fontSize: 11,
                }}
              >
                {cardPresentation.showUnresolvedGaps ? (
                  <span
                    style={{
                      color:
                        project.unresolvedGapCount > 0
                          ? 'var(--warning)'
                          : 'var(--success)',
                      fontWeight: 700,
                    }}
                  >
                    {t('history.projects.unresolvedGaps', {
                      count: project.unresolvedGapCount,
                    })}
                  </span>
                ) : (
                  <span style={{ color: 'var(--danger)', fontWeight: 700 }}>
                    {cardPresentation.rescanMessage}
                  </span>
                )}
                <span
                  style={{
                    color:
                      project.openTaskCount > 0
                        ? 'var(--accent-primary)'
                        : 'var(--text-muted)',
                    fontWeight: 700,
                  }}
                >
                  {t('history.projects.openTasks', {
                    count: project.openTaskCount,
                  })}
                </span>
                <span>
                  {t('history.projects.updated')}:{' '}
                  {formatDate(project.lastUpdatedAt)}
                </span>
              </div>

              {cardPresentation.showUnnamedProjectPrompt && (
                <div
                  style={{
                    marginTop: -8,
                    marginBottom: 16,
                    color: 'var(--warning)',
                    fontSize: 12,
                  }}
                >
                  This project still has its default name. Open the workspace to
                  give it a descriptive name.
                </div>
              )}

              <button
                className="button button-primary"
                onClick={() => onOpen(project)}
                style={{ width: '100%' }}
              >
                {t('history.projects.openWorkspace')}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

const filterLabelStyle = {
  display: 'grid',
  gap: 5,
  color: 'var(--text-secondary)',
  fontSize: 12,
  fontWeight: 600,
};

const filterControlStyle = {
  width: '100%',
  minHeight: 38,
  padding: '8px 10px',
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-primary)',
  font: 'inherit',
};
